import { DEFAULT_DESIGN_PRESET } from './designPresets';
import {
  defaultDesignCustomization,
  normalizeDesignCustomization,
  tableStyleList,
} from './designCustomization';
import {
  DEFAULT_NAVIGATION_LAYOUT,
  navigationLayoutList,
} from '../../shared/ui/layout/navigationLayouts';

export const WORKSPACE_MODE_STORAGE_KEY = 'bes-ui-workspace-modes';

function createWorkspaceMode({
  id,
  label,
  description,
  presetId = DEFAULT_DESIGN_PRESET,
  showAssistPanel = false,
  tags = [],
  customization = {},
  custom = false,
  createdAt = null,
}) {
  return {
    id,
    label,
    description,
    presetId,
    showAssistPanel,
    tags,
    custom: Boolean(custom),
    createdAt,
    customization: normalizeDesignCustomization({
      ...defaultDesignCustomization,
      ...customization,
    }),
  };
}

export const DEFAULT_WORKSPACE_MODE = 'cleanStart';

export const workspaceModes = {
  cleanStart: createWorkspaceMode({
    id: 'cleanStart',
    label: 'Clean Start',
    description:
      'A minimal white workspace with quieter tables, softer surfaces, and no extra helper chrome turned on by default.',
    tags: ['Default', 'Minimal', 'Light'],
    presetId: 'formalNeutral',
    customization: {
      navigationLayout: 'cleanDashboard',
      density: 'comfortable',
      radius: 'soft',
      panelStyle: 'flat',
      tableStyle: 'clean',
      documentTone: 'minimal',
      sidebarCollapsed: false,
      showFlowBanner: false,
      showSidebarQuickActions: false,
      showSidebarRecentPages: false,
      showTopbarContext: false,
      showHelperText: false,
      showSnapshotPanels: false,
      showUsageNotesPanels: false,
      showLedgerNotesPanel: false,
      showCollectionsViewPanel: false,
      showCatalogHealthPanel: false,
      showDataQualityPanel: false,
      showTaxCurrentMixPanel: false,
      showOperatorNotesPanel: false,
      showFormSafetyPanel: false,
      showUnsavedAlerts: true,
    },
  }),
  classicBilling: createWorkspaceMode({
    id: 'classicBilling',
    label: 'Classic Billing',
    description:
      'The balanced everyday workspace: default Hatched shell, strong document presentation, and standard list density.',
    tags: ['Default', 'Balanced', 'Billing'],
    presetId: 'formalNeutral',
    customization: {
      navigationLayout: DEFAULT_NAVIGATION_LAYOUT,
      density: 'comfortable',
      radius: 'rounded',
      panelStyle: 'elevated',
      tableStyle: 'striped',
      documentTone: 'studio',
      sidebarCollapsed: false,
      showFlowBanner: true,
      showSidebarQuickActions: true,
      showSidebarRecentPages: true,
      showTopbarContext: true,
      showHelperText: true,
      showSnapshotPanels: true,
      showUsageNotesPanels: true,
      showLedgerNotesPanel: true,
      showCollectionsViewPanel: true,
      showCatalogHealthPanel: true,
      showDataQualityPanel: true,
      showTaxCurrentMixPanel: true,
      showOperatorNotesPanel: true,
      showFormSafetyPanel: true,
      showUnsavedAlerts: true,
    },
  }),
  operatorMode: createWorkspaceMode({
    id: 'operatorMode',
    label: 'Operator Mode',
    description:
      'High-contrast dark workspace tuned for frequent navigation, denser scanning, and action-heavy billing tasks.',
    tags: ['Dark', 'High focus', 'Fast ops'],
    presetId: 'formalDark',
    customization: {
      navigationLayout: 'workspaceDark',
      density: 'compact',
      radius: 'sharp',
      panelStyle: 'flat',
      tableStyle: 'contrast',
      documentTone: 'minimal',
      sidebarCollapsed: false,
      showFlowBanner: false,
      showSidebarQuickActions: true,
      showSidebarRecentPages: true,
      showTopbarContext: true,
      showHelperText: false,
      showSnapshotPanels: false,
      showUsageNotesPanels: false,
      showLedgerNotesPanel: false,
      showCollectionsViewPanel: false,
      showCatalogHealthPanel: false,
      showDataQualityPanel: false,
      showTaxCurrentMixPanel: false,
      showOperatorNotesPanel: false,
      showFormSafetyPanel: false,
      showUnsavedAlerts: true,
    },
  }),
  compactReview: createWorkspaceMode({
    id: 'compactReview',
    label: 'Compact Review',
    description:
      'A calmer audit-style setup with compact spacing, quieter panels, and a cleaner list treatment for review work.',
    tags: ['Review', 'Calm', 'Compact'],
    presetId: 'sageLedger',
    customization: {
      navigationLayout: 'cleanDashboard',
      density: 'compact',
      radius: 'soft',
      panelStyle: 'outlined',
      tableStyle: 'clean',
      documentTone: 'minimal',
      sidebarCollapsed: false,
      showFlowBanner: false,
      showSidebarQuickActions: false,
      showSidebarRecentPages: false,
      showTopbarContext: true,
      showHelperText: false,
      showSnapshotPanels: false,
      showUsageNotesPanels: false,
      showLedgerNotesPanel: false,
      showCollectionsViewPanel: false,
      showCatalogHealthPanel: false,
      showDataQualityPanel: false,
      showTaxCurrentMixPanel: false,
      showOperatorNotesPanel: false,
      showFormSafetyPanel: false,
      showUnsavedAlerts: true,
    },
  }),
  mobileFirst: createWorkspaceMode({
    id: 'mobileFirst',
    label: 'Mobile First',
    description:
      'A friendlier lightweight bundle with card-style tables, softer controls, and reduced chrome for smaller screens.',
    tags: ['Mobile', 'Cards', 'Lightweight'],
    presetId: 'playfulAccent',
    customization: {
      navigationLayout: 'componentSidebar',
      density: 'compact',
      radius: 'soft',
      panelStyle: 'flat',
      tableStyle: 'mobileCards',
      documentTone: 'minimal',
      sidebarCollapsed: false,
      showFlowBanner: false,
      showSidebarQuickActions: false,
      showSidebarRecentPages: false,
      showTopbarContext: false,
      showHelperText: false,
      showSnapshotPanels: false,
      showUsageNotesPanels: false,
      showLedgerNotesPanel: false,
      showCollectionsViewPanel: false,
      showCatalogHealthPanel: false,
      showDataQualityPanel: false,
      showTaxCurrentMixPanel: false,
      showOperatorNotesPanel: false,
      showFormSafetyPanel: false,
      showUnsavedAlerts: true,
    },
  }),
};

function slugifyModeLabel(label) {
  return String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, 36);
}

function normalizeStoredWorkspaceMode(rawMode, index) {
  if (!rawMode || typeof rawMode !== 'object') {
    return null;
  }

  const label = String(rawMode.label || '').trim();
  if (!label) {
    return null;
  }

  const description = String(rawMode.description || '').trim();
  const normalizedId =
    typeof rawMode.id === 'string' && rawMode.id.trim()
      ? rawMode.id.trim()
      : `custom-${slugifyModeLabel(label) || 'workspace'}-${index + 1}`;
  const tags = Array.isArray(rawMode.tags)
    ? rawMode.tags.filter(Boolean).slice(0, 5)
    : ['Custom'];
  if (!tags.includes('Custom')) {
    tags.unshift('Custom');
  }

  return createWorkspaceMode({
    id: normalizedId,
    label,
    description: description || 'Custom workspace mode',
    presetId: rawMode.presetId || DEFAULT_DESIGN_PRESET,
    showAssistPanel: Boolean(rawMode.showAssistPanel),
    tags,
    customization: rawMode.customization || {},
    custom: true,
    createdAt: rawMode.createdAt || null,
  });
}

export function normalizeStoredWorkspaceModes(rawModes) {
  if (!Array.isArray(rawModes)) {
    return [];
  }

  const seen = new Set();
  return rawModes
    .map((mode, index) => normalizeStoredWorkspaceMode(mode, index))
    .filter((mode) => {
      if (!mode || seen.has(mode.id)) {
        return false;
      }
      seen.add(mode.id);
      return true;
    });
}

export function getStoredWorkspaceModes(storage = typeof window !== 'undefined' ? window.localStorage : null) {
  if (!storage) {
    return [];
  }

  try {
    const rawValue = storage.getItem(WORKSPACE_MODE_STORAGE_KEY);
    if (!rawValue) {
      return [];
    }
    return normalizeStoredWorkspaceModes(JSON.parse(rawValue));
  } catch (_error) {
    return [];
  }
}

export function persistWorkspaceModes(
  workspaceModeEntries,
  storage = typeof window !== 'undefined' ? window.localStorage : null
) {
  if (!storage) {
    return;
  }

  storage.setItem(
    WORKSPACE_MODE_STORAGE_KEY,
    JSON.stringify(normalizeStoredWorkspaceModes(workspaceModeEntries))
  );
}

export function composeWorkspaceModes(customModes = []) {
  return [...Object.values(workspaceModes), ...normalizeStoredWorkspaceModes(customModes)];
}

export const workspaceModeList = composeWorkspaceModes();

export const workspaceModeDisplayMeta = {
  navigationLayout: Object.fromEntries(
    navigationLayoutList.map((layout) => [layout.id, layout.label])
  ),
  tableStyle: Object.fromEntries(tableStyleList.map((style) => [style.value, style.label])),
};

export function getWorkspaceMode(modeId, modeList = workspaceModeList) {
  return modeList.find((mode) => mode.id === modeId) || modeList.find((mode) => mode.id === DEFAULT_WORKSPACE_MODE);
}

export function resolveWorkspaceMode(
  presetId,
  customization,
  showAssistPanel,
  modeList = workspaceModeList
) {
  const resolvedCustomization = normalizeDesignCustomization(customization);

  return (
    modeList.find((mode) => {
      if (mode.presetId !== presetId || mode.showAssistPanel !== showAssistPanel) {
        return false;
      }

      return Object.keys(defaultDesignCustomization).every(
        (key) => mode.customization[key] === resolvedCustomization[key]
      );
    }) || null
  );
}

export function buildCustomWorkspaceMode({
  id,
  label,
  description,
  tags = ['Custom', 'Saved'],
  presetId,
  showAssistPanel,
  customization,
}) {
  const safeLabel = String(label || '').trim() || 'Custom workspace mode';
  const modeId =
    typeof id === 'string' && id.trim()
      ? id.trim()
      : `custom-${slugifyModeLabel(safeLabel) || 'workspace'}-${Date.now()}`;

  return createWorkspaceMode({
    id: modeId,
    label: safeLabel,
    description: String(description || '').trim() || 'Saved custom workspace mode',
    tags,
    presetId: presetId || DEFAULT_DESIGN_PRESET,
    showAssistPanel: Boolean(showAssistPanel),
    customization: customization || defaultDesignCustomization,
    custom: true,
    createdAt: new Date().toISOString(),
  });
}
